import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { ListPagination } from '@/components/shared/ListPagination'
import { QuoteStatusBadge } from '@/components/shared/StatusBadges'
import { DateRangeFilter } from '@/components/clients/tabs/DateRangeFilter'
import {
  ClientTabSection,
  clientTabFilterOptionClass,
  clientTabFilterTriggerClass,
  clientTabPrimaryBtnClass,
  clientTabSearchInputClass,
  clientTabTableShadow,
  clientTabTdClass,
  clientTabFirstTdClass,
  clientTabThClass,
  clientTabActionsThClass,
  clientTabActionsTdClass,
  clientTabViewDetailsBtnClass,
} from '@/components/clients/tabs/ClientTabSection'
import { useClientQuotes } from '@/hooks/useClients'
import { useListParams } from '@/hooks/useListParams'
import { formatCurrency, formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import { QUOTE_STATUS, type QuoteStatus } from '@/types'

const STATUS_OPTIONS = Object.values(QUOTE_STATUS)
const ALL = 'all'

type Filters = {
  status?: string
  date_from?: string
  date_to?: string
}

const COLUMN_COUNT = 6

// 2.4.7 Client details — quotes tab (Figma 2889:57355).
export function QuotesTab({
  clientId,
  canCreateQuote = false,
}: {
  clientId: string
  canCreateQuote?: boolean
}) {
  const { t } = useTranslation(['clients', 'enums', 'common'])
  const navigate = useNavigate()
  const {
    page,
    setPage,
    limit,
    setLimit,
    search,
    onSearchChange,
    filters,
    setFilters,
    query,
  } = useListParams<Filters>()
  const { data, isLoading, isError } = useClientQuotes(clientId, query)

  const quotes = data?.data ?? []
  const openQuote = (quoteId: string) => navigate(`/quotes/${quoteId}`)

  return (
    <ClientTabSection
      title={t('detail.tabs.quotes')}
      toolbar={
        <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
          <div className='flex min-w-0 flex-wrap items-center gap-4'>
            <DateRangeFilter
              appearance='field'
              triggerClassName={FORM_CONTROL_FOCUS_CLASS}
              from={filters.date_from}
              to={filters.date_to}
              onChange={setFilters}
            />
            <Select
              value={filters.status ?? ALL}
              onValueChange={v =>
                setFilters({ status: v === ALL ? undefined : v })
              }
            >
              <SelectTrigger className={clientTabFilterTriggerClass}>
                <SelectValue placeholder={t('tabFilters.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL} className={clientTabFilterOptionClass}>
                  {t('tabFilters.allStatuses')}
                </SelectItem>
                {STATUS_OPTIONS.map(s => (
                  <SelectItem
                    key={s}
                    value={s}
                    className={clientTabFilterOptionClass}
                  >
                    {t(`enums:quoteStatus.${s}`, { defaultValue: s })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='flex flex-col gap-4 sm:flex-row sm:items-center'>
            <div className='relative min-w-0 flex-1'>
              <Search className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#3d434e]' />
              <Input
                value={search}
                onChange={e => onSearchChange(e.target.value)}
                placeholder={t('quotes.searchPlaceholder')}
                maxLength={50}
                className={clientTabSearchInputClass}
              />
            </div>
            {canCreateQuote && (
              <Button
                className={clientTabPrimaryBtnClass}
                onClick={() => navigate(`/quotes/new?client_id=${clientId}`)}
              >
                <Plus className='size-4' /> {t('quotes.addNew')}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div
        className={cn(
          'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white',
          clientTabTableShadow
        )}
      >
        <div className='overflow-x-auto'>
          <table className='w-full caption-bottom text-sm [&_th:nth-child(3)]:text-center [&_td:nth-child(3)]:text-center [&_th:nth-child(5)]:text-center [&_td:nth-child(5)]:text-center'>
            <TableHeader>
              <TableRow className='border-0'>
                <TableHead className={clientTabThClass}>
                  {t('quotes.columns.reference')}
                </TableHead>
                <TableHead className={clientTabThClass}>
                  {t('quotes.columns.title')}
                </TableHead>
                <TableHead className={clientTabThClass}>
                  {t('quotes.columns.amount')}
                </TableHead>
                <TableHead className={clientTabThClass}>
                  {t('quotes.columns.createdDate')}
                </TableHead>
                <TableHead className={clientTabThClass}>
                  {t('quotes.columns.status')}
                </TableHead>
                <TableHead className={clientTabActionsThClass}>
                  {t('list.columns.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i} className='border-0'>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className={clientTabTdClass}
                    >
                      <Skeleton className='h-6 w-full' />
                    </TableCell>
                  </TableRow>
                ))}

              {isError && !isLoading && (
                <TableRow className='border-0'>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      clientTabTdClass,
                      'py-10 text-center text-destructive'
                    )}
                  >
                    {t('quotes.error')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && !isError && quotes.length === 0 && (
                <TableRow className='border-0'>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      clientTabTdClass,
                      'py-10 text-center text-muted-foreground'
                    )}
                  >
                    {t('quotes.empty')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading &&
                !isError &&
                quotes.map(q => (
                  <TableRow
                    key={q.id}
                    className='group relative cursor-pointer border-0'
                    onClick={() => openQuote(q.id)}
                  >
                    <TableCell className={clientTabFirstTdClass}>
                      <div className='text-sm font-medium leading-[1.4] text-[#171a20] group-hover:text-[#ff5a1f]'>
                        {q.number ?? q.id.slice(0, 8)}
                      </div>
                    </TableCell>
                    <TableCell className={clientTabTdClass}>
                      <span className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                        {q.title?.trim() || '—'}
                      </span>
                    </TableCell>
                    <TableCell className={clientTabTdClass}>
                      <span className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                        {formatCurrency(q.total, 'EUR', 2)}
                      </span>
                    </TableCell>
                    <TableCell className={clientTabTdClass}>
                      <span className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                        {formatDate(q.created_at)}
                      </span>
                    </TableCell>
                    <TableCell className={clientTabTdClass}>
                      <QuoteStatusBadge status={q.status as QuoteStatus} />
                    </TableCell>
                    <TableCell className={clientTabActionsTdClass}>
                      <button
                        type='button'
                        onClick={e => {
                          e.stopPropagation()
                          openQuote(q.id)
                        }}
                        className={clientTabViewDetailsBtnClass}
                      >
                        {t('common:actions.view')}
                        <ArrowRight className='size-4 shrink-0' />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </table>
        </div>

        <ListPagination
          className='border-t border-[#e7e9ef]'
          metadata={data?.metadata}
          page={page}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
          showShowingRange={false}
        />
      </div>
    </ClientTabSection>
  )
}
